import { useCallback, useState } from 'react';
import { buildTripPayload } from './buildTripPayload';
import { createInitialValues } from './initialValues';
import type { TripWizardConfig, WizardValues } from './types';

function resolveDmcId(
  config: TripWizardConfig,
  values: WizardValues,
): string | null {
  return config.dmc.mode === 'fixed' ? config.dmc.id : values.dmcId;
}

function extractMessage(err: unknown): string {
  const anyErr = err as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return (
    anyErr?.response?.data?.message ?? anyErr?.message ?? 'Something went wrong'
  );
}

export function useTripWizard({
  config,
  seed,
  editId,
}: {
  config: TripWizardConfig;
  seed?: Partial<WizardValues>;
  editId?: string;
}) {
  // Opening the wizard mounts a fresh instance: a create flow always starts
  // clean, a seeded wizard (edit / duplicate) from its trip. No resume cache —
  // a stale half-filled trip resurfacing on the next open is worse than
  // retyping.
  const [values, setValues] = useState<WizardValues>(() =>
    createInitialValues(config, seed),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setError(null);
    setValues(createInitialValues(config, seed));
  }, [config, seed]);

  const set = useCallback(
    <K extends keyof WizardValues>(key: K, value: WizardValues[K]) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const patch = useCallback((partial: Partial<WizardValues>) => {
    setValues((prev) => ({ ...prev, ...partial }));
  }, []);

  const submit = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = buildTripPayload(values, {
        dmcId: resolveDmcId(config, values),
        locale: values.locale ?? config.locale,
        id: editId,
      });
      const res = await config.saveTrip(payload);
      // The host reuses these values as its live tripFilters: hand it the same
      // clamped date the payload was saved with, or a template's past date
      // would survive locally and every composer query would 400.
      config.onSaved(res.data.tripId, {
        ...values,
        departureDate: payload.departureDate,
      });
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }, [config, editId, values]);

  return { values, set, patch, reset, submit, submitting, error };
}

export type TripWizardState = ReturnType<typeof useTripWizard>;
